import { tableAnatomy as parts } from '@chakra-ui/anatomy'

import { mode } from '@chakra-ui/theme-tools'
import type {
  PartsStyleFunction,
  PartsStyleObject,
  SystemStyleObject,
} from '@chakra-ui/theme-tools'

const baseStyle: PartsStyleObject<typeof parts> = {
  table: {
    fontVariantNumeric: 'lining-nums tabular-nums',
    borderCollapse: 'collapse',
    width: 'full',
  },
  th: {
    fontFamily: 'heading',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 'wider',
    textAlign: 'start',
  },
  td: {
    textAlign: 'start',
  },
  caption: {
    mt: 4,
    fontFamily: 'heading',
    textAlign: 'center',
    fontWeight: 'medium',
  },
}

const numericStyles: SystemStyleObject = {
  '&[data-is-numeric=true]': {
    textAlign: 'end',
  },
}

const variantSimple: PartsStyleFunction<typeof parts> = props => {
  const { colorScheme: c } = props

  return {
    th: {
      color: mode('gray.600', 'gray.400')(props),
      borderBottom: '1px',
      borderColor: mode(`${c}.100`, `${c}.700`)(props),
      ...numericStyles,
    },
    td: {
      borderBottom: '1px',
      borderColor: mode(`${c}.100`, `${c}.700`)(props),
      ...numericStyles,
    },
    caption: {
      color: mode('gray.600', 'gray.100')(props),
    },
    tfoot: {
      tr: {
        '&:last-of-type': {
          th: { borderBottomWidth: 0 },
        },
      },
    },
  }
}

const variantStripe: PartsStyleFunction<typeof parts> = props => {
  const { colorScheme: c } = props

  return {
    th: {
      color: mode('gray.600', 'gray.400')(props),
      borderBottom: '1px',
      borderColor: mode(`${c}.100`, `${c}.700`)(props),
      ...numericStyles,
    },
    td: {
      borderBottom: '1px',
      borderColor: mode(`${c}.100`, `${c}.700`)(props),
      ...numericStyles,
    },
    caption: {
      color: mode('gray.600', 'gray.100')(props),
    },
    tbody: {
      tr: {
        '&:nth-of-type(odd)': {
          'th, td': {
            borderBottomWidth: '1px',
            borderColor: mode(`${c}.100`, `${c}.700`)(props),
          },
          td: {
            background: mode(`${c}.100`, `${c}.700`)(props),
          },
        },
      },
    },
    tfoot: {
      tr: {
        '&:last-of-type': {
          th: { borderBottomWidth: 0 },
        },
      },
    },
  }
}

const variantCustom: PartsStyleFunction<typeof parts> = props => ({
  table: {
    borderRadius: 'md',
  },
  thead: {
    backgroundColor: 'appPaleGreyThree',
    th: {
      borderWidth: '0px',
      '&:first-of-type': {
        borderTopLeftRadius: 'md',
      },
      '&:last-of-type': {
        borderTopRightRadius: 'md',
      },
    },
  },
  tfoot: {
    borderBottomRadius: 'md',
    backgroundColor: 'appPaleGreyThree',
    tr: {
      borderRadius: '0px',
      borderWidth: '0px',
      '&:first-of-type': {
        borderBottomLeftRadius: 'md',
      },
      '&:last-of-type': {
        borderBottomRightRadius: 'md',
      },
    },
    th: {
      borderWidth: '0px',
      '&:first-of-type': {
        borderBottomLeftRadius: 'md',
      },
      '&:last-of-type': {
        borderBottomRightRadius: 'md',
      },
    },
  },
  th: {
    color: 'appDarkIndigo',
    fontWeight: 'medium',
    textTransform: 'capitalise',
    letterSpacing: 'normal',
    borderWidth: '1px',
    borderColor: 'appPaleGreyThree',
    ...numericStyles,
  },
  td: {
    borderWidth: '1px',
    borderColor: 'appPaleGreyThree',
    ...numericStyles,
  },
  caption: {
    color: mode('gray.600', 'gray.100')(props),
  },
})

const variants = {
  simple: variantSimple,
  striped: variantStripe,
  custom: variantCustom,
  unstyled: {},
}

const sizes: Record<string, PartsStyleObject<typeof parts>> = {
  sm: {
    th: {
      px: '4',
      py: '1',
      lineHeight: '4',
      fontSize: 'xs',
    },
    td: {
      px: '4',
      py: '2',
      fontSize: 'sm',
      lineHeight: '4',
    },
    caption: {
      px: '4',
      py: '2',
      fontSize: 'xs',
    },
  },
  md: {
    th: {
      px: '6',
      py: '3',
      lineHeight: '4',
      fontSize: 'md',
    },
    td: {
      px: '6',
      py: '4',
      lineHeight: '5',
    },
    caption: {
      px: '6',
      py: '2',
      fontSize: 'sm',
    },
  },
  lg: {
    th: {
      px: '8',
      py: '4',
      lineHeight: '5',
      fontSize: 'sm',
    },
    td: {
      px: '8',
      py: '5',
      lineHeight: '6',
    },
    caption: {
      px: '6',
      py: '2',
      fontSize: 'md',
    },
  },
}

const defaultProps = {
  variant: 'custom',
  size: 'md',
  colorScheme: 'gray',
}

export default {
  parts: parts.keys,
  baseStyle,
  variants,
  sizes,
  defaultProps,
}
